import { useEffect, useId, useRef, useState } from "react";
import { Menu, MessageCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";
import { useLocation, useNavigate } from "react-router-dom";

const labels = {
  pt: {
    navigation: "Navegação principal",
    openMenu: "Abrir menu",
    closeMenu: "Fechar menu",
    language: "Idioma",
    decision: "Sua compra",
    calcination: "Calcinação",
    products: "Tipos de cal",
    coverage: "Atendimento",
    differentials: "Diferenciais",
    whatsapp: "Falar com a Quimical",
    whatsappMessage: "Olá. Gostaria de falar com a Quimical sobre fornecimento de cal industrial.",
  },
  en: {
    navigation: "Main navigation",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    language: "Language",
    decision: "Your purchase",
    calcination: "Calcination",
    products: "Lime types",
    coverage: "Service area",
    differentials: "Differentials",
    whatsapp: "Talk to Quimical",
    whatsappMessage: "Hello. I would like to talk to Quimical about industrial lime supply.",
  },
  es: {
    navigation: "Navegación principal",
    openMenu: "Abrir menú",
    closeMenu: "Cerrar menú",
    language: "Idioma",
    decision: "Su compra",
    calcination: "Calcinación",
    products: "Tipos de cal",
    coverage: "Atención",
    differentials: "Diferenciales",
    whatsapp: "Hablar con Quimical",
    whatsappMessage: "Hola. Me gustaría hablar con Quimical sobre suministro de cal industrial.",
  },
};

const Header = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const l = labels[language];
  const links = [
    { href: "/#decisao", label: l.decision },
    { href: "/#calcinacao", label: l.calcination },
    { href: "/#produtos", label: l.products },
    { href: "/#atendimento", label: l.coverage },
    { href: "/#diferenciais", label: l.differentials },
  ];

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  const goHome = () => {
    setMenuOpen(false);
    navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openWhatsApp = () => {
    window.open(
      `https://wa.me/5537998606150?text=${encodeURIComponent(l.whatsappMessage)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <header
      className="z-50 border-b border-slate-200/70 bg-white"
      onKeyDown={(event) => {
        if (event.key === "Escape" && menuOpen) {
          setMenuOpen(false);
          menuButtonRef.current?.focus();
        }
      }}
    >
      <div className="container mx-auto px-4">
        <div className="flex h-20 items-center justify-between md:h-24">
          <button
            type="button"
            onClick={goHome}
            className="flex-shrink-0 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            aria-label="Quimical - página inicial"
          >
            <img
              src="/media/logoespecialhome-new.jpg"
              alt="Quimical"
              className="h-16 w-auto max-w-36 object-contain md:h-20 lg:max-w-none"
              width="96"
              height="96"
            />
          </button>

          <nav className="hidden items-center gap-5 lg:flex" aria-label={l.navigation}>
            {links.map((link) => (
              <a key={link.href} href={link.href} className="text-sm font-medium text-slate-600 transition-colors hover:text-slate-950">{link.label}</a>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <div className="hidden lg:block">
              <LanguageSelector />
            </div>
            <Button
              size="sm"
              className="h-11 min-w-11 rounded-full px-3 lg:h-9 lg:px-4"
              onClick={openWhatsApp}
              aria-label={l.whatsapp}
            >
              <MessageCircle className="h-4 w-4 lg:mr-2" />
              <span className="hidden lg:inline">{l.whatsapp}</span>
            </Button>
            <Button
              ref={menuButtonRef}
              type="button"
              variant="outline"
              className="h-11 w-11 shrink-0 p-0 lg:hidden"
              aria-label={menuOpen ? l.closeMenu : l.openMenu}
              aria-expanded={menuOpen}
              aria-controls={menuId}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
            </Button>
          </div>
        </div>
        <div id={menuId} hidden={!menuOpen} className="border-t border-slate-200 pb-4 lg:hidden">
          <nav aria-label={l.navigation} className="py-2">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="flex min-h-11 items-center rounded px-3 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
              >
                {link.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center justify-between border-t border-slate-100 px-3 pt-3">
            <span className="text-sm text-slate-600">{l.language}</span>
            <LanguageSelector />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;


